import { BIRTH_DATE_COPY_EN } from "@shared/birthDateCopy";
import type { BookingMessages } from "./types";

export const en: BookingMessages = {
  meta: {
    title: "Request a pitch online",
    description: "Request a pitch online – Campingplatz Mainkur on the Rhine.",
  },
  common: {
    night: "night",
    nights: "nights",
    day: "day",
    days: "days",
    loading: "Loading…",
    back: "Back",
    continue: "Continue",
    edit: "Edit",
    selectDate: "Select date",
    arrival: "Arrival",
    departure: "Departure",
    adults: "Adults",
    adultsAgeHint: "aged 15+",
    children: "Children",
    childrenAgeHint: "aged 2–14",
    dogs: "Dogs",
    inclVat: "incl. VAT",
    approx: "Approx.",
    licensePlate: "License plate",
    dash: "–",
  },
  steps: {
    period: "Dates",
    vehicle: "Vehicle",
    pitch: "Pitch",
    details: "Guests",
    contact: "Contact",
    summary: "Summary",
  },
  stepHeadings: {
    period: {
      title: "Travel dates",
      intro: "When would you like to arrive and leave?",
    },
    vehicle: {
      title: "Arrival & vehicle",
      intro: "How are you travelling? This determines which pitches are suitable.",
    },
    pitch: {
      title: "Choose a pitch",
      intro: "These categories are available for your dates.",
    },
    details: {
      title: "Guests & preferences",
      intro: "Who is travelling – and do you have any requests for the pitch?",
    },
    contact: {
      title: "Your contact details",
      intro: "We need your details for the confirmation and any questions.",
    },
    summary: {
      title: "Summary",
      intro: "Please check your details before sending the request.",
    },
  },
  flow: {
    pageTitle: "Request a pitch online",
    pageSubtitle: "Non-binding request – you will receive a reply by email.",
    stepOf: (current, total) => `Step ${current} of ${total}`,
    soldOutDaysHint:
      "Fully booked days are greyed out in the calendar. Arrival is not possible on those days, departure is.",
    vehicleLengthHint: "Total length including trailer or towing vehicle.",
    vehicleDetailsTitle: "Vehicle details",
    noVehicleDetailsNeeded: "No further details are needed for this arrival type.",
    arrivalTypeUnavailable: (label, arrival, departure) =>
      `No pitch is available for ${label} from ${arrival} to ${departure}. Please choose another arrival type or different dates.`,
    categoryLabel: "Category",
    travellersTitle: "Guests",
    optionalLabel: "optional",
    quickSelect: (min, max) => `Back to quick select ${min}–${max}`,
    knownCompanionSelect: "Use a person from previous stays",
    knownCompanionManual: "Enter manually",
    licensePlateFormatHint: "Spaces and hyphens are added automatically.",
    summaryIntro: "You can still change any detail via “Edit”.",
    pitchAvailableHint: "Pitches are available – choose your category in the next step.",
    thankYouTitle: "Thank you!",
    bookingNumber: "Booking number",
    configLoadError: "Online booking settings could not be loaded.",
    priceCalcError: "Price could not be calculated.",
    holdCreateError: "The reservation could not be created.",
    availabilityLoadError: "Availability could not be loaded.",
    alternativesLoadError: "Alternative dates could not be loaded.",
    submitError: "Booking request could not be sent.",
    staleAlternative:
      "These dates are no longer available. Please choose another suggestion or different dates.",
    holdExpired: "Your reservation has expired. Please select a pitch category again.",
    selectCategory: "Please select a pitch category.",
    noPitchWithSuggestions:
      "No pitch is available for these dates. Please choose one of the suggestions or different dates.",
    noPitch:
      "No pitches are available for the selected dates. Please choose different dates.",
    noPitchForArrival:
      "No pitch is currently available for this arrival type. Please choose another arrival type or different dates.",
    selectAvailablePeriod: "Please select available dates for this category.",
    acceptTerms: "Please accept the booking terms.",
    savedData: "Your saved details",
    contactData: "Your contact details",
    missingFieldsHint: "Please complete the missing details.",
    savedProfileHint:
      "We use your saved details. You can complete any missing required fields below.",
    yourDataField: (field: string) => `Your details: ${field}`,
    knownCompanionHint: "You can reuse companions from previous stays.",
    companionsSummary: "Travel companions",
    companionDetailsHint:
      "Details of the other travellers. You are already recorded as the main guest.",
    sending: "Sending…",
    submitRequest: "Submit request",
    checkingAvailability: "Checking availability…",
    loadingCategories: "Loading categories…",
    selectPeriodFirst: "Select available dates first",
    selectPitch: "Choose a pitch",
    checkAvailability: "Check availability",
    messageLabel: "Message to us",
    messagePlaceholder: "e.g. expected arrival time or special requests",
    termsPrefix: "I have read the",
    termsAnd: "and the",
    termsSuffix:
      "and am submitting a non-binding booking request.",
    termsLink: "cancellation terms",
    privacyLink: "privacy notice",
    legalClose: "Close",
    partyTitle: "Stay",
    agesTitle: "Travellers' ages",
    agesHint: "Children are aged 2–14, adults are aged 15 and over.",
    primaryGuestAge: "Main guest age",
    adultAge: (n) => `Adult ${n} age`,
    childAge: (n) => `Child ${n} age`,
    addonsTitle: "Add-ons",
    featuresTitle: "Pitch preferences",
    featuresHint: "Non-binding – we consider preferences subject to availability.",
    summaryTitle: "Summary",
    summaryPeriod: "Dates",
    summaryVehicle: "Pitch & arrival",
    summaryPitch: "Pitch",
    summaryParty: "Party",
    summaryContact: "Contact",
    summaryMessage: "Message",
    summaryPrice: "Price",
    next: "Continue",
    previous: "Back",
  },
  validation: {
    selectDates: "Please select arrival and departure dates.",
    departureAfterArrival: "Departure must be after arrival.",
    arrivalNotPast: "Arrival cannot be in the past.",
    sameDayCutoffBlocked:
      "Online bookings for arrival today are no longer possible from {time}.",
    minStayOne: "A minimum stay of 1 night is required.",
    minStayMany: (n) => `A minimum stay of ${n} nights is required for the selected period.`,
    soldOutArrival:
      "The campsite is fully booked on this day. Arrival on this day is not possible.",
    licensePlate: "Please check the license plate (example: AB-C 1234).",
    ageRequired: "Please enter the age of all travellers.",
    childAgeRange: "Children must be between 2 and 14 years old on the arrival date.",
    childAgeMin: "Children must be at least 2 years old on the arrival date.",
    adultAgeMin: "Adults must be at least 15 years old on the arrival date.",
    primaryAgeMin: "The main guest must be at least 18 years old on the arrival date.",
    primaryBirthDateRequired: "Please enter the main guest's date of birth.",
    companionBirthDateRequired: (slot) => `${slot}: date of birth is required.`,
    invalidBirthDate: "Please check the date of birth.",
    companionInvalidBirthDate: (slot) => `${slot}: please check the date of birth.`,
    companionFieldRequired: (slot, field) => `${slot}: ${field} is required.`,
    primaryGuestRequiredFields: (fields) =>
      `Please complete the following required fields: ${fields}.`,
    emailIncomplete: "Please check the email address – the format looks incomplete.",
    emailUnusual: "Please check the email address – the format looks unusual.",
  },
  birthDate: BIRTH_DATE_COPY_EN,
  availability: {
    checkingPhrases: [
      "Checking availability…",
      "Looking for suitable pitches…",
      "Comparing occupancy…",
    ],
    alternativePhrases: ["Searching for free dates…", "Checking the next 14 days…"],
    searchingPitches: "We are checking which pitches are free for your dates.",
    searchingAlternatives: "We are checking for free dates in the next 14 days.",
    periodSoldOutTitle: "No pitch is available for these dates",
    periodSoldOutBody: (arrival, departure, nights, nightLabel) =>
      `We cannot offer a pitch from ${arrival} to ${departure} (${nights} ${nightLabel}).`,
    periodSoldOutHint: "Please choose different dates.",
    tryWithoutElectricity: "Check without electricity",
    soldOutTitle: "Everything is taken for these dates",
    soldOutBody: (arrival, departure, nights, nightLabel) =>
      `All pitches are taken from ${arrival} to ${departure} (${nights} ${nightLabel}).`,
    soldOutHint:
      "Below you can see free dates close to your preferred period. Alternatively, choose different dates.",
    noAlternativesTitle: (category) =>
      category ? `No free dates nearby for “${category}”` : "No free dates nearby",
    noAlternativesBody: (category) =>
      category
        ? `Nothing is available for “${category}” in the next 14 days.`
        : "Nothing is available in the next 14 days.",
    noVehiclePitchTitle: "No suitable pitch for your vehicle",
    noVehiclePitchBody: (vehicleLabel) =>
      vehicleLabel
        ? `We have no suitable pitch for ${vehicleLabel} in the selected period.`
        : "We have no suitable pitch for your vehicle in the selected period.",
    noVehiclePitchHint: "Please check the vehicle details or choose different dates.",
    alternativesTitle: (category) =>
      category ? `Free dates for “${category}”` : "Free dates nearby",
    alternativesHint: (nights, nightLabel) =>
      `Requested: ${nights} ${nightLabel}. One click applies the dates.`,
    selectPeriod: "Use these dates",
    sameArrival: "Same arrival",
    sameDeparture: "Same departure",
    arrivalLater: (days, unit) => `Arrival ${days} ${unit} later`,
    arrivalEarlier: (days, unit) => `Arrival ${days} ${unit} earlier`,
    periodLater: (days, unit) => `Period ${days} ${unit} later`,
    periodEarlier: (days, unit) => `Period ${days} ${unit} earlier`,
    samePeriod: "Same period",
    nightsWanted: (nights, wanted) =>
      `${nights} ${nights === 1 ? "night" : "nights"} (${wanted} requested)`,
    placeFree: "pitch available",
    placesFree: "pitches available",
    inCategory: " in this category",
    choosePeriod: "Please choose different dates in the calendar.",
  },
  arrivalType: {
    legend: "Arrival type",
    noneAvailable:
      "No arrival types are currently enabled for online booking. Please contact the campsite.",
  },
  electricity: {
    legend: "Electricity",
    ariaLabel: "Choose electricity option",
    withLabel: "With electricity",
    withHint: "CEE socket at the pitch",
    withoutLabel: "Without electricity",
    withoutHint: "No electricity needed at the pitch",
  },
  price: {
    calculating: "Calculating price…",
    overview: "Price overview",
    estimatedTotal: "Estimated total price",
    perNight: "/ night",
    positionTotal: "Total",
    lineNightFormula: (qty, nights, nightLabel, unitPrice) =>
      `${qty} × ${nights} ${nightLabel} × ${unitPrice}`,
    lineUnitFormula: (qty, unitPrice) => `${qty} × ${unitPrice}`,
    disclaimer:
      "Estimated price incl. VAT. The final amount is set with the confirmation.",
    pending: "Price to follow",
    pendingHint:
      "Prices for this period are not published yet. You can still book. We will tell you the price as soon as it is set.",
  },
  guestAuth: {
    portal: "My guest portal",
    returningGuests: "Returning guests",
    alreadyBooked: "Stayed with us before?",
    alreadyBookedHint: "Sign in to reuse your saved details and previous travel companions.",
    login: "Sign in",
    loginCodeHint: "You will receive a sign-in code by email.",
    manageStays: "Manage stays",
    manageStaysHint: "Sign in to view upcoming and past stays.",
    loginNow: "Sign in now",
    returningGuestsRegion: "Sign in for returning guests",
    inlineRegion: "Note for returning guests",
  },
  login: {
    title: "Sign in",
    subtitleBooking:
      "Been here before? Sign in with your email – your details will be used in the booking.",
    subtitleDefault:
      "Sign in with your email address. We will send you a one-time code.",
    emailLabel: "Email address",
    codeLabel: "Sign-in code",
    requestCode: "Request code by email",
    requestingCode: "Sending…",
    verify: "Sign in",
    verifying: "Verifying…",
    backToBooking: "Back to booking",
    toBooking: "To online booking",
    requestError: "Code could not be requested.",
    loginError: "Sign-in failed.",
  },
  portal: {
    title: "My guest portal",
    loadError: "Data could not be loaded.",
    sectionsAria: "Portal sections",
    upcoming: "Upcoming stays",
    upcomingEmpty: "No planned stays. Book your next holiday online.",
    past: "Past stays",
    pastEmpty: "No past stays yet.",
    profile: "My details",
    logout: "Sign out",
    bookNow: "New reservation",
  },
  bookingDetail: {
    loadError: "Stay could not be loaded.",
    backToPortal: "← Back to my stays",
  },
  fields: {
    title: "Salutation",
    first_name: "First name",
    middle_name: "Middle name",
    last_name: "Last name",
    company_name: "Company name",
    vat_id: "VAT ID",
    email: "Email",
    phone: "Phone",
    license_plate: "License plate",
    age: "Age",
    date_of_birth: "Date of birth",
    nationality: "Nationality",
    street: "Street",
    house_number: "House number",
    house_number_short: "No.",
    address_search: "(Search)",
    address_placeholder: "Enter an address and select a suggestion…",
    address_line_2: "Address line 2",
    postal_code: "Postcode",
    city: "City",
    state_region: "State/region",
    arrival: "Arrival",
    departure: "Departure",
    nights: "Nights",
    travelers: "Travellers",
    arrival_type: "Arrival type",
    electricity: "Electricity",
    travel_data: "Trip details",
    price: "Price",
    your_message: "Your message",
    main_guest: "Main guest",
    pitch: "Pitch",
    yes: "Yes",
    no: "No",
  },
  vehicleFields: {
    license_plate: "License plate",
    vehicle_length_m: "Length",
    vehicle_width_m: "Width",
    vehicle_height_m: "Height",
    vehicle_weight_kg: "Weight",
    tent_size: "Tent size",
    vehicle_length_placeholder: "e.g. 7.5",
    tent_size_placeholder: "e.g. 4 × 6 m",
  },
  companionFields: {
    title: "Salutation",
    first_name: "First name",
    middle_name: "Middle name",
    last_name: "Last name",
    company_name: "Company name",
    vat_id: "VAT ID",
    email: "Email",
    phone: "Phone",
    license_plate: "License plate",
    age: "Age",
    date_of_birth: "Date of birth",
    nationality: "Nationality",
    street: "Street",
    house_number: "House number",
    house_number_short: "No.",
    address_search: "(Search)",
    address_placeholder: "Enter an address and select a suggestion…",
    address_line_2: "Address line 2",
    postal_code: "Postcode",
    city: "City",
    state_region: "State/region",
    country_code: "Country (ISO)",
    adult: "Adult",
    child: "Child",
    no_details: "No details provided",
  },
  salutation: {
    herr: "Mr",
    frau: "Ms",
    select: "Please select …",
  },
  bookingStatus: {
    draft: "Draft",
    requested: "Requested",
    reserved: "Reserved",
    confirmed: "Confirmed",
    checked_in: "Checked in",
    checked_out: "Checked out",
    cancelled: "Cancelled",
    rejected: "Declined",
    no_show: "No show",
  },
  guestPortalVehicle: {
    motorhome: "Motorhome",
    caravan: "Caravan",
    tent_car: "Tent with car",
    tent_bike: "Tent with bike",
    kommend: "Upcoming",
    aktuell: "Current",
    vergangen: "Past",
    storniert: "Cancelled",
    electricity_yes: "Yes",
    electricity_no: "No",
  },
  season: {
    outsideSeason: "The selected dates are outside the season.",
    bookableOnly: "Online booking is available during the season",
    closingDayDepartureOnly: "On the last day of the season only departure is possible.",
  },
  periodSelected: {
    title: "Period selected",
    nights: (n, nightLabel) => `${n} ${nightLabel}`,
  },
  holdTimer: {
    label: "Reserved for",
    hint: "We hold the pitch for you for this long.",
    expired: "Your reservation has expired. Please select a pitch category again.",
  },
  category: {
    recommended: "Recommended",
    from: "from",
    perNight: "/ night",
    perPeriod: {
      night: "/ night",
      week: "/ week",
      month: "/ month",
      quarter: "/ quarter",
      semi_annual: "/ half-year",
      annual: "/ year",
    },
    priceHint: "incl. VAT, 1 adult",
    total: "Total",
    priceFollows: "Price to follow",
    select: "Select",
    selected: "Selected",
    unavailable: "Unavailable",
    available: (count) =>
      count === 1 ? `${count} pitch available` : `${count} pitches available`,
  },
  phoneComplete: {
    pageTitle: "Confirm your reservation",
    pageSubtitle: "Please enter your full details and confirm the booking as binding.",
    loading: "Loading…",
    invalidLink: "This link is invalid or has expired.",
    toBooking: "To the booking page",
    alreadyDone: "This booking has already been confirmed. Thank you!",
    confirmed:
      "Thank you – you have confirmed your booking. We look forward to your stay.",
    booking: "Booking",
    period: "Period",
    category: "Category",
    deadline: (when) => `Please confirm by ${when} – afterwards the pitch is released.`,
    yourData: "Your details",
    electricity: "Electricity hook-up requested",
    wishes: "Pitch preferences",
    wishesHint: "We take your preferences into account when a matching pitch is free.",
    vehicle: "Vehicle",
    notes: "Notes (optional)",
    firstNameRequired: "Please enter your first name.",
    lastNameRequired: "Please enter your last name.",
    arrivalType: "Arrival type",
    arrivalTypeRequired: "Please choose how you will arrive.",
    categoryRequired: "Please choose a pitch category.",
    vehicleRequired: "Please complete all required vehicle fields.",
    saveError: "Saving failed. Please try again.",
    saving: "Saving…",
    submit: "Confirm booking",
    submitAndPay: "Save details and pay the deposit",
    bookingNumber: "Booking number",
    termsPrefix: "I confirm this booking as binding. I have read the",
    termsSuffix: "and accept them.",
    depositTitle: "Deposit",
    depositOnline: "After saving we will take you to the online deposit payment.",
    depositOffline: (amount) =>
      `The deposit of ${amount} is due under the payment terms. Online payment is not set up.`,
    depositEstimate: (label, amount) => `${label}: ${amount}`,
    payDeposit: "Pay deposit now",
    paying: "Continue to payment…",
    paymentCancelled: "The payment was cancelled. You can start the deposit again.",
    paymentPending:
      "We have not confirmed the deposit yet. Please reload in a moment or contact reception.",
    confirmedDepositDue: (amount) =>
      `Thank you. Your booking is confirmed. Please note the deposit of ${amount}.`,
    confirmedPaid: "Thank you. Your details are saved and the deposit has been received.",
  },
  comfortCheckIn: {
    pageTitle: "Comfort check-in",
    pageSubtitle: "Enter your licence plate and pay for your stay online.",
    loading: "Loading…",
    invalidLink: "This comfort check-in link is invalid.",
    toBooking: "To the booking page",
    booking: "Booking",
    period: "Period",
    pitch: "Pitch",
    guest: "Guest",
    stayTotal: "Stay price",
    amountDue: "Due now",
    alreadyPaid: "This stay is already paid. Please confirm the licence plate.",
    pricePending:
      "No price has been published for this stay yet. You can complete check-in; we will send the price later.",
    priceFollows: "Price to follow",
    licensePlate: "Licence plate",
    licensePlateCountry: "Country",
    plateRequired: "Please enter the licence plate.",
    pay: "Save plate and pay",
    completeFree: "Complete comfort check-in",
    submitting: "Preparing…",
    redirecting: "Continue to payment…",
    completed: "Thank you. Your comfort check-in is complete. We look forward to your arrival.",
    alreadyDone: "Comfort check-in for this booking is already complete.",
    bookingNumber: "Booking number",
    paymentCancelled: "Payment was cancelled. You can try again.",
    confirmingPayment: "Confirming payment…",
    paymentPending:
      "We have not confirmed the payment yet. Please reload in a moment or contact reception.",
    stripeUnavailable: "Online payment is currently unavailable. Please contact reception.",
    saveError: "Comfort check-in could not be saved. Please try again.",
  },
};
